import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AppUser } from 'Src/views/App/App';
import { FormBuilderV2 } from '../FormBuilderV2/FormBuilderV2';
import { FormDefinition } from 'Src/types/formSchema';
import { formDefinitionToLegacyFields, legacyFieldsToFormDefinition } from 'Src/helpers/form/legacyFormAdapter';
import { validateFormDefinitionStructure } from 'Src/helpers/form/formDefinitionValidation';
import { uploadFormFile, uploadFormImage } from 'Src/helpers/form/formMedia';
import { getCreatedAtWithTime, getUpdatedAtWithTime } from 'Src/helpers/accessor/accessor';
import { Loader } from '../Loader/Loader';
import { SimpleModal } from '../SimpleModal/SimpleModal';
import { HelpIconButton } from '../HelpIconButton/HelpIconButton';
import { UnsavedChangesModal } from '../UnsavedChangesModal/UnsavedChangesModal';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { Pagination2 } from 'Src/components/Pagination/Pagination2';
import {
  createFormTemplate,
  deleteFormTemplate,
  FormTemplate,
  FormTemplateType,
  getFormTemplatesForTypes,
  updateFormTemplate
} from 'Src/helpers/service/admin/formTemplates';
import {
  createFormTemplateTag,
  deleteFormTemplateTag,
  FormTemplateTag,
  getAllFormTemplateTags,
  updateFormTemplateTag
} from 'Src/helpers/service/admin/formTemplateTags';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faFileAlt,
  faPlus,
  faTags,
  faTimes,
  faTrash,
  faTrophy
} from '@fortawesome/free-solid-svg-icons';
import './FormTemplatesManager.css';

type Props = {
  user: AppUser;
  type?: FormTemplateType;
  types?: FormTemplateType[];
  title: string;
  defaultName?: string;
  definitionTitle?: string;
};

type TemplateTypeConfig = {
  label: string;
  defaultName: string;
  definitionTitle: string;
  color: string;
  icon: any;
};

type TemplateStatusFilter = 'ALL' | 'DEFAULT' | 'CUSTOM';
type ManagerTab = 'TEMPLATES' | 'TAGS';

const TEMPLATE_ROLE_OPTIONS = ['ADMIN', 'MANAGER', 'TEACHER', 'COACH'];
const TEMPLATE_PAGE_LIMIT = 10;
const TEMPLATE_COLUMNS = [
  { field: 'template', label: 'Template', locked: true },
  { field: 'type', label: 'Type' },
  { field: 'tags', label: 'Tags' },
  { field: 'status', label: 'Status' },
  { field: 'updatedAt', label: 'Last updated' }
];
const TEMPLATE_COLUMNS_VIEW_KEY = 'form-builder.templates';
const DEFAULT_TAG_COLOR = '#227EBD';
const TAG_PALETTE = [
  { bg: '#e8f3ff', border: '#a9d5ff', text: '#075da8' },
  { bg: '#edf8ef', border: '#a9ddb3', text: '#256b34' },
  { bg: '#fff4de', border: '#f3c77f', text: '#8a5600' },
  { bg: '#f2edff', border: '#c9b8ff', text: '#5b38b6' },
  { bg: '#ffeef4', border: '#f3b4c9', text: '#a92555' },
  { bg: '#eaf8f7', border: '#9bd8d3', text: '#12635f' },
  { bg: '#f4f6f8', border: '#c9d2dc', text: '#334155' }
];
const TAG_COLOR_PRESETS = ['#227EBD', '#268049', '#F08C1A', '#7B5BD6', '#D64545', '#0F8793'];

const TEMPLATE_TYPE_CONFIG: Record<FormTemplateType, TemplateTypeConfig> = {
  CONSENT: {
    label: 'Consent request',
    defaultName: 'Default consent request form',
    definitionTitle: 'Consent request questions',
    color: '#2563EB',
    icon: faFileAlt
  },
  CLUB_BOOKING: {
    label: 'Club booking',
    defaultName: 'Default club booking form',
    definitionTitle: 'Club booking questions',
    color: '#059669',
    icon: faCalendarCheck
  },
  TOURNAMENT_CONFIRMATION: {
    label: 'Tournament confirmation',
    defaultName: 'Default tournament confirmation form',
    definitionTitle: 'Tournament confirmation request',
    color: '#7C3AED',
    icon: faTrophy
  }
};

const getConfig = (type: FormTemplateType): TemplateTypeConfig => TEMPLATE_TYPE_CONFIG[type];

const toDefinition = (template: FormTemplate, title: string): FormDefinition =>
  template.schemaVersion === 2 && template.formDefinition
    ? template.formDefinition
    : legacyFieldsToFormDefinition(template.fields || [], { title });

const normalizeTag = (tag: string): string =>
  String(tag || '')
    .trim()
    .replace(/\s+/g, ' ')
    .slice(0, 40);

const normalizeTags = (tags: string[] = []): string[] => {
  const result: string[] = [];
  tags
    .map(normalizeTag)
    .filter(Boolean)
    .forEach(tag => {
      const exists = result.some(item => item.toLowerCase() === tag.toLowerCase());
      if (!exists) result.push(tag);
    });
  return result.slice(0, 20);
};

const normalizeTagIds = (tagIds: string[] = []): string[] => {
  const result: string[] = [];
  tagIds
    .map(tagId => String(tagId || '').trim())
    .filter(Boolean)
    .forEach(tagId => {
      if (!result.includes(tagId)) result.push(tagId);
    });
  return result.slice(0, 20);
};

const stableSerialize = (value: any): string => {
  const normalize = (item: any): any => {
    if (Array.isArray(item)) return item.map(normalize);
    if (item && typeof item === 'object') {
      return Object.keys(item)
        .sort()
        .reduce((result, key) => {
          if (typeof item[key] !== 'undefined') result[key] = normalize(item[key]);
          return result;
        }, {} as Record<string, any>);
    }
    return item;
  };
  return JSON.stringify(normalize(value));
};

const getTagColor = (tag: string) => {
  const value = String(tag || '');
  const hash = value.split('').reduce((result, char) => result + char.charCodeAt(0), 0);
  return TAG_PALETTE[hash % TAG_PALETTE.length];
};

const getTagStyle = (tag: string): React.CSSProperties => {
  const color = getTagColor(tag);
  return { backgroundColor: color.bg, borderColor: color.border, color: color.text };
};

const getAuditUserText = (name?: string, email?: string): string => {
  const cleanName = String(name || '').trim();
  const cleanEmail = String(email || '').trim();
  if (cleanName && cleanEmail && cleanName !== cleanEmail) return `${cleanName} (${cleanEmail})`;
  return cleanName || cleanEmail || '-';
};

const countQuestions = (definition: FormDefinition): number =>
  definition.elements.filter(element => !['DIVIDER', 'PAGE_BREAK'].includes(element.type)).length;

const validate = (definition: FormDefinition): string => {
  const issues = validateFormDefinitionStructure(definition);
  if (issues.length) return issues.map(issue => issue.message).join(' ');
  const untitled = definition.elements.find(
    element => !['DIVIDER', 'PAGE_BREAK'].includes(element.type) && !String(element.title || '').trim()
  );
  if (untitled) return 'Every question and content element must have a title.';
  const invalidChoice = definition.elements.find(element => {
    if (!['SINGLE_CHOICE', 'MULTIPLE_CHOICE'].includes(element.type)) return false;
    const options = element.config?.options || [];
    return !options.length || options.some(option => !String(option.label || '').trim());
  });
  return invalidChoice ? 'Choice questions must contain at least one non-empty option.' : '';
};

export const FormTemplatesManager: React.FC<Props> = ({ user, type, types, title, defaultName, definitionTitle }) => {
  const history = useHistory();
  const location = useLocation();
  const activeTypes = React.useMemo<FormTemplateType[]>(() => {
    const requested: FormTemplateType[] = types && types.length ? types : type ? [type] : ['CONSENT'];
    return requested.filter((item, index) => requested.indexOf(item) === index);
  }, [type, types]);

  const [templates, setTemplates] = React.useState<FormTemplate[]>([]);
  const [listTemplates, setListTemplates] = React.useState<FormTemplate[]>([]);
  const [listReloadToken, setListReloadToken] = React.useState(0);
  const [availableTags, setAvailableTags] = React.useState<FormTemplateTag[]>([]);
  const [activeTab, setActiveTab] = React.useState<ManagerTab>('TEMPLATES');
  const [editingKey, setEditingKey] = React.useState('');
  const [isEditorDirty, setIsEditorDirty] = React.useState(false);
  const [definition, setDefinition] = React.useState<FormDefinition | undefined>();
  const [name, setName] = React.useState('');
  const [editableByRoles, setEditableByRoles] = React.useState<string[]>(['ADMIN']);
  const [templateTagIds, setTemplateTagIds] = React.useState<string[]>([]);
  const [newTagName, setNewTagName] = React.useState('');
  const [newTagColor, setNewTagColor] = React.useState(DEFAULT_TAG_COLOR);
  const [renameColor, setRenameColor] = React.useState(DEFAULT_TAG_COLOR);
  const [filterName, setFilterName] = React.useState('');
  const [appliedFilterName, setAppliedFilterName] = React.useState('');
  const [filterType, setFilterType] = React.useState<FormTemplateType | 'ALL'>('ALL');
  const [filterStatus, setFilterStatus] = React.useState<TemplateStatusFilter>('ALL');
  const [filterTag, setFilterTag] = React.useState('ALL');
  const [tagSearch, setTagSearch] = React.useState('');
  const [appliedTagSearch, setAppliedTagSearch] = React.useState('');
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [draftFilterStatus, setDraftFilterStatus] = React.useState<TemplateStatusFilter>('ALL');
  const [draftFilterTag, setDraftFilterTag] = React.useState('ALL');
  const [isColumnsOpen, setIsColumnsOpen] = React.useState(false);
  const [actionMenuKey, setActionMenuKey] = React.useState('');
  const [hiddenColumns, setHiddenColumns] = React.useState<string[]>(() =>
    loadHiddenColumns(user, TEMPLATE_COLUMNS_VIEW_KEY)
  );
  const [currentPage, setCurrentPage] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState('');
  const [isHelpOpen, setIsHelpOpen] = React.useState(false);
  const [isCreateTypeOpen, setIsCreateTypeOpen] = React.useState(false);
  const [tagModalMode, setTagModalMode] = React.useState<'CREATE' | 'EDIT' | ''>('');
  const [isTagDiscardOpen, setIsTagDiscardOpen] = React.useState(false);
  const [renamingTag, setRenamingTag] = React.useState('');
  const [renameValue, setRenameValue] = React.useState('');
  const [detailsTemplate, setDetailsTemplate] = React.useState<FormTemplate | undefined>();
  const [templatePendingDelete, setTemplatePendingDelete] = React.useState<FormTemplate | undefined>();
  const [pendingExitPath, setPendingExitPath] = React.useState('');
  const skipRouteOpenRef = React.useRef(false);
  const allowNavigationRef = React.useRef(false);
  const editorSnapshotRef = React.useRef('');
  const builderUserInteractedRef = React.useRef(false);
  const columnsRef = React.useRef<HTMLDivElement>(null);
  const actionMenuRef = React.useRef<HTMLDivElement>(null);
  const tagModalSnapshotRef = React.useRef('');

  React.useEffect(() => {
    const onOutsideClick = (event: MouseEvent) => {
      if (columnsRef.current && !columnsRef.current.contains(event.target as Node)) setIsColumnsOpen(false);
      if (actionMenuRef.current && !actionMenuRef.current.contains(event.target as Node)) setActionMenuKey('');
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsColumnsOpen(false);
        setActionMenuKey('');
      }
    };
    document.addEventListener('mousedown', onOutsideClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onOutsideClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const editorRoute = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      templateType: params.get('formTemplateType') as FormTemplateType | null,
      templateId: params.get('formTemplateId') || ''
    };
  }, [location.search]);

  const editorPath = React.useCallback(
    (template: FormTemplate) =>
      `${location.pathname}?formTemplateType=${template.templateType}&formTemplateId=${template.id}`,
    [location.pathname]
  );

  const listPath = React.useCallback(() => location.pathname, [location.pathname]);
  const getTemplateKey = (template: FormTemplate) => `${template.templateType}:${template.id}`;
  const editingTemplate = templates.find(item => getTemplateKey(item) === editingKey);
  const getEditorSnapshot = React.useCallback(
    (nextName: string, roles: string[], tagIds: string[], nextDefinition?: FormDefinition) =>
      stableSerialize({
        name: nextName,
        roles: [...roles].sort(),
        tagIds: [...tagIds].sort(),
        definition: nextDefinition
      }),
    []
  );
  const hasUnsavedChanges = Boolean(
    editingTemplate &&
      definition &&
      isEditorDirty &&
      editorSnapshotRef.current !== getEditorSnapshot(name, editableByRoles, templateTagIds, definition)
  );

  const tagByName = React.useMemo(() => {
    const map = new Map<string, FormTemplateTag>();
    availableTags.forEach(tag => map.set(tag.tagName.toLowerCase(), tag));
    return map;
  }, [availableTags]);

  const tagById = React.useMemo(() => {
    const map = new Map<string, FormTemplateTag>();
    availableTags.forEach(tag => map.set(tag.id, tag));
    return map;
  }, [availableTags]);

  const getTemplateTagNames = React.useCallback(
    (template: FormTemplate): string[] => {
      const namesFromIds = (template.tagIds || [])
        .map(tagId => tagById.get(String(tagId))?.tagName || '')
        .filter(Boolean);
      if (namesFromIds.length) return normalizeTags(namesFromIds);
      return normalizeTags((template.tags || []).map((tag: any) => (typeof tag === 'string' ? tag : tag.tagName)));
    },
    [tagById]
  );

  const getTemplateTagIds = React.useCallback(
    (template: FormTemplate): string[] => {
      const idsFromField = (template.tagIds || []).map(tagId => String(tagId || '').trim()).filter(Boolean);
      if (idsFromField.length) return idsFromField.filter((tagId, index) => idsFromField.indexOf(tagId) === index);
      const idsFromTags = (template.tags || [])
        .map((tag: any) => {
          if (typeof tag === 'string') return tagByName.get(tag.toLowerCase())?.id || '';
          return String(tag.id || tag._id || '').trim();
        })
        .filter(Boolean);
      return idsFromTags.filter((tagId, index) => idsFromTags.indexOf(tagId) === index);
    },
    [tagByName]
  );

  const getTagStyleForName = React.useCallback(
    (tagName: string): React.CSSProperties => {
      const tag = tagByName.get(String(tagName || '').toLowerCase());
      if (!tag || !/^#[0-9a-fA-F]{6}$/.test(tag.color || '')) return getTagStyle(tagName);
      const color = tag.color;
      const hex = color.replace('#', '');
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return {
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.12)`,
        borderColor: `rgba(${r}, ${g}, ${b}, 0.45)`,
        color
      };
    },
    [tagByName]
  );

  const getDefaultName = React.useCallback(
    (templateType: FormTemplateType): string => defaultName || getConfig(templateType).defaultName,
    [defaultName]
  );

  const getDefinitionTitle = React.useCallback(
    (templateType: FormTemplateType): string => definitionTitle || getConfig(templateType).definitionTitle,
    [definitionTitle]
  );

  const allTags = React.useMemo(() => availableTags.map(tag => tag.tagName).sort((a, b) => a.localeCompare(b)), [
    availableTags
  ]);

  const openEditor = React.useCallback(
    (template: FormTemplate, options: { syncUrl?: boolean } = {}) => {
      const currentDefinitionTitle = getDefinitionTitle(template.templateType);
      setEditingKey(getTemplateKey(template));
      const nextDefinition = toDefinition(template, currentDefinitionTitle);
      const nextName = template.name || getDefaultName(template.templateType);
      const nextRoles = template.editableByRoles?.length ? template.editableByRoles : ['ADMIN'];
      const nextTagIds = getTemplateTagIds(template);
      setName(nextName);
      setEditableByRoles(nextRoles);
      setTemplateTagIds(nextTagIds);
      setDefinition(nextDefinition);
      setIsEditorDirty(false);
      builderUserInteractedRef.current = false;
      editorSnapshotRef.current = getEditorSnapshot(nextName, nextRoles, nextTagIds, nextDefinition);
      setError('');
      if (options.syncUrl !== false) {
        const nextPath = editorPath(template);
        if (`${location.pathname}${location.search}` !== nextPath) history.push(nextPath);
      }
    },
    [
      editorPath,
      getDefaultName,
      getDefinitionTitle,
      getEditorSnapshot,
      getTemplateTagIds,
      history,
      location.pathname,
      location.search
    ]
  );

  const closeEditor = () => {
    if (saving) return;
    if (hasUnsavedChanges) {
      setPendingExitPath(listPath());
      return;
    }
    skipRouteOpenRef.current = true;
    setEditingKey('');
    setDefinition(undefined);
    setIsEditorDirty(false);
    setError('');
    if (location.search) history.push(listPath());
  };

  const load = React.useCallback(() => {
    setLoading(true);
    return Promise.all([getFormTemplatesForTypes(user, activeTypes, ''), getAllFormTemplateTags(user)])
      .then(([items, tags]) => {
        setTemplates(items);
        setListTemplates(items);
        setListReloadToken(current => current + 1);
        setAvailableTags(tags);
        setLoading(false);
        return items;
      })
      .catch(() => {
        setLoading(false);
        setError('Failed to load form templates.');
        return [] as FormTemplate[];
      });
  }, [user, activeTypes]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    let isCancelled = false;
    const requestedTypes = filterType === 'ALL' ? activeTypes : activeTypes.filter(item => item === filterType);
    getFormTemplatesForTypes(user, requestedTypes, {
        search: appliedFilterName.trim(),
        status: filterStatus,
        tagId: filterTag === 'ALL' ? undefined : filterTag
      })
        .then(items => {
          if (!isCancelled) setListTemplates(items);
        })
        .catch(() => {
          if (!isCancelled) setError('Failed to filter form templates.');
        });

    return () => {
      isCancelled = true;
    };
  }, [activeTypes, appliedFilterName, filterStatus, filterTag, filterType, listReloadToken, user]);

  React.useEffect(() => {
    const routeKey =
      editorRoute.templateType && editorRoute.templateId ? `${editorRoute.templateType}:${editorRoute.templateId}` : '';

    if (!routeKey) {
      skipRouteOpenRef.current = false;
      if (editingKey) {
          setEditingKey('');
          setDefinition(undefined);
          setIsEditorDirty(false);
        setError('');
      }
      return;
    }

    if (skipRouteOpenRef.current || editingKey === routeKey || !templates.length) return;

    const template = templates.find(item => getTemplateKey(item) === routeKey);
    if (template) openEditor(template, { syncUrl: false });
  }, [editorRoute.templateId, editorRoute.templateType, editingKey, openEditor, templates]);
  React.useEffect(() => {
    setCurrentPage(1);
  }, [appliedFilterName, filterType, filterStatus, filterTag, templates.length]);

  React.useEffect(() => {
    if (!hasUnsavedChanges) return undefined;
    const unblock = history.block(nextLocation => {
      if (allowNavigationRef.current) return undefined;
      setPendingExitPath(`${nextLocation.pathname}${nextLocation.search}`);
      return false;
    });
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      unblock();
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [hasUnsavedChanges, history]);

  const save = (makeDefault?: boolean) => {
    if (!definition || !editingTemplate) return;
    const validationError = validate(definition);
    if (validationError) {
      setError(validationError);
      return;
    }
    const currentType = editingTemplate.templateType;
    setSaving(true);
    updateFormTemplate(user, currentType, editingTemplate.id, {
      name: String(name || '').trim() || getDefaultName(currentType),
      ...(makeDefault ? { isDefault: true } : {}),
      editableByRoles,
      tagIds: normalizeTagIds(templateTagIds),
      schemaVersion: 2,
      formDefinition: definition,
      fields: formDefinitionToLegacyFields(definition) as any
    })
      .then(() => {
        editorSnapshotRef.current = getEditorSnapshot(name, editableByRoles, templateTagIds, definition);
        setIsEditorDirty(false);
        return load();
      })
      .then(
        () => setSaving(false),
        () => {
          setSaving(false);
          setError('Failed to save form template.');
        }
      );
  };

  const create = (templateType: FormTemplateType) => {
    const blank = legacyFieldsToFormDefinition([], { title: getDefinitionTitle(templateType) });
    const nextNumber = templates.filter(item => item.templateType === templateType).length + 1;
    setSaving(true);
    setIsCreateTypeOpen(false);
    createFormTemplate(user, templateType, {
      name: `${getDefaultName(templateType)} ${nextNumber}`,
      editableByRoles: ['ADMIN'],
      tagIds: [],
      schemaVersion: 2,
      formDefinition: blank,
      fields: []
    })
      .then(created => load().then(() => openEditor(created, { syncUrl: true })))
      .then(
        () => setSaving(false),
        () => {
          setSaving(false);
          setError('Failed to create form template.');
        }
      );
  };

  const onNewTemplateClick = () => {
    if (activeTypes.length === 1) {
      create(activeTypes[0]);
      return;
    }
    setIsCreateTypeOpen(true);
  };

  const requestRemove = (template: FormTemplate) => {
    if (!template.id) return;
    setTemplatePendingDelete(template);
  };

  const duplicateTemplate = (template: FormTemplate) => {
    const templateDefinition = toDefinition(template, getDefinitionTitle(template.templateType));
    setSaving(true);
    createFormTemplate(user, template.templateType, {
      name: `${template.name || getDefaultName(template.templateType)} (copy)`,
      isDefault: false,
      editableByRoles: template.editableByRoles?.length ? template.editableByRoles : ['ADMIN'],
      tagIds: normalizeTagIds(getTemplateTagIds(template)),
      schemaVersion: 2,
      formDefinition: templateDefinition,
      fields: formDefinitionToLegacyFields(templateDefinition) as any
    })
      .then(() => load())
      .then(
        () => setSaving(false),
        () => {
          setSaving(false);
          setError('Failed to duplicate form template.');
        }
      );
  };
  const remove = () => {
    const template = templatePendingDelete;
    if (!template?.id) return;
    const templateKey = getTemplateKey(template);
    setSaving(true);
    deleteFormTemplate(user, template.templateType, template.id)
      .then(() => load())
      .then(
        () => {
          setSaving(false);
          setTemplatePendingDelete(undefined);
          if (editingKey === templateKey) {
            allowNavigationRef.current = true;
            editorSnapshotRef.current = '';
            setEditingKey('');
            setDefinition(undefined);
            setIsEditorDirty(false);
            setError('');
            if (location.search) history.push(listPath());
            setTimeout(() => {
              allowNavigationRef.current = false;
            }, 0);
          }
        },
        () => {
          setSaving(false);
          setError('Failed to delete form template.');
        }
      );
  };

  const createTag = () => {
    const tagName = normalizeTag(newTagName);
    if (!tagName) return;
    setSaving(true);
    createFormTemplateTag(user, { tagName, color: newTagColor })
      .then(() => load())
      .then(
        () => {
          setSaving(false);
          setNewTagName('');
          setNewTagColor(DEFAULT_TAG_COLOR);
          setTagModalMode('');
        },
        () => {
          setSaving(false);
          setError('Failed to create template tag. Tag names must be unique.');
        }
      );
  };

  const renameTag = (oldTag: string) => {
    const tag = tagByName.get(oldTag.toLowerCase());
    const nextTag = normalizeTag(renameValue);
    if (!tag || !nextTag) {
      setRenamingTag('');
      setRenameValue('');
      return;
    }
    setSaving(true);
    updateFormTemplateTag(user, tag.id, { tagName: nextTag, color: renameColor })
      .then(() => load())
      .then(
        () => {
          setSaving(false);
          setRenamingTag('');
          setRenameValue('');
          setTagModalMode('');
        },
        () => {
          setSaving(false);
          setError('Failed to update template tag. Tag names must be unique.');
        }
      );
  };

  const deleteTag = (tagToDelete: string) => {
    const tag = tagByName.get(tagToDelete.toLowerCase());
    if (!tag) return;
    setSaving(true);
    deleteFormTemplateTag(user, tag.id)
      .then(() => load())
      .then(
        () => setSaving(false),
        () => {
          setSaving(false);
          setError('Failed to delete template tag.');
        }
      );
  };

  const openCreateTagModal = () => {
    setNewTagName('');
    setNewTagColor(DEFAULT_TAG_COLOR);
    setRenamingTag('');
    tagModalSnapshotRef.current = JSON.stringify({ name: '', color: DEFAULT_TAG_COLOR });
    setTagModalMode('CREATE');
  };

  const openEditTagModal = (tag: string) => {
    const currentTag = tagByName.get(tag.toLowerCase());
    setRenamingTag(tag);
    setRenameValue(tag);
    setRenameColor(currentTag?.color || DEFAULT_TAG_COLOR);
    tagModalSnapshotRef.current = JSON.stringify({
      name: tag,
      color: currentTag?.color || DEFAULT_TAG_COLOR
    });
    setTagModalMode('EDIT');
  };

  const discardTagChanges = () => {
    if (saving) return;
    setIsTagDiscardOpen(false);
    setTagModalMode('');
    setRenamingTag('');
    setRenameValue('');
    tagModalSnapshotRef.current = '';
  };

  const closeTagModal = () => {
    if (saving) return;
    const currentSnapshot = JSON.stringify({
      name: tagModalMode === 'EDIT' ? renameValue : newTagName,
      color: tagModalMode === 'EDIT' ? renameColor : newTagColor
    });
    if (currentSnapshot !== tagModalSnapshotRef.current) {
      setIsTagDiscardOpen(true);
      return;
    }
    discardTagChanges();
  };

  const filteredTemplates = React.useMemo(() => {
    const nameSearch = appliedFilterName.trim().toLowerCase();
    return listTemplates.filter(template => {
      const nameMatches =
        !nameSearch ||
        String(template.name || '')
          .toLowerCase()
          .includes(nameSearch);
      const typeMatches = filterType === 'ALL' || template.templateType === filterType;
      const statusMatches =
        filterStatus === 'ALL' ||
        (filterStatus === 'DEFAULT' && template.isDefault) ||
        (filterStatus === 'CUSTOM' && !template.isDefault);
      const tagMatches = filterTag === 'ALL' || getTemplateTagIds(template).includes(filterTag);
      return nameMatches && typeMatches && statusMatches && tagMatches;
    });
  }, [listTemplates, filterName, filterType, filterStatus, filterTag, getTemplateTagIds]);

  const pageTemplates = filteredTemplates.slice(
    (currentPage - 1) * TEMPLATE_PAGE_LIMIT,
    currentPage * TEMPLATE_PAGE_LIMIT
  );
  const isFiltered = Boolean(appliedFilterName || filterType !== 'ALL' || filterStatus !== 'ALL' || filterTag !== 'ALL');
  const activeDrawerFilterCount = Number(filterStatus !== 'ALL') + Number(filterTag !== 'ALL');
  const visibleTags = allTags.filter(tag => tag.toLowerCase().includes(appliedTagSearch.trim().toLowerCase()));
  const isColumnVisible = (field: string) => !hiddenColumns.includes(field);
  const visibleColumnCount = TEMPLATE_COLUMNS.filter(column => column.locked || isColumnVisible(column.field)).length;
  const persistHiddenColumns = (next: string[]) => {
    setHiddenColumns(next);
    saveHiddenColumns(user, TEMPLATE_COLUMNS_VIEW_KEY, next);
  };
  const toggleColumn = (field: string) => {
    const next = hiddenColumns.includes(field)
      ? hiddenColumns.filter(item => item !== field)
      : [...hiddenColumns, field];
    persistHiddenColumns(next);
  };
  const openFilters = () => {
    setDraftFilterStatus(filterStatus);
    setDraftFilterTag(filterTag);
    setIsFilterOpen(true);
  };
  const closeFilters = () => setIsFilterOpen(false);
  const applyFilters = () => {
    setFilterStatus(draftFilterStatus);
    setFilterTag(draftFilterTag);
    setCurrentPage(1);
    setIsFilterOpen(false);
  };
  const discardChanges = () => {
    const nextPath = pendingExitPath || listPath();
    allowNavigationRef.current = true;
    skipRouteOpenRef.current = true;
    editorSnapshotRef.current = '';
    setPendingExitPath('');
    setEditingKey('');
    setDefinition(undefined);
    setIsEditorDirty(false);
    setError('');
    history.push(nextPath);
    setTimeout(() => {
      allowNavigationRef.current = false;
    }, 0);
  };

  const deleteConfirmationModal = (
    <SimpleModal
      isOpen={!!templatePendingDelete}
      title="Delete template?"
      onCloseClick={() => setTemplatePendingDelete(undefined)}
      onBackdropClick={() => setTemplatePendingDelete(undefined)}
      buttonCancelText="Cancel"
      buttonText={saving ? 'Deleting...' : 'Delete template'}
      onButtonClick={saving ? undefined : remove}
      customClass="ftmDeleteModal ftmDeleteModal--danger"
      dialogStyle={{ maxWidth: 480 }}
      isBodyBold={false}
      body={
        templatePendingDelete ? (
          <div className="ftmDeleteBody">
            <div className="ftmDeleteIcon" aria-hidden="true">
              !
            </div>
            <div className="ftmDeleteMessage">
              <div className="ftmDeleteTitle">
                {templatePendingDelete.name || getDefaultName(templatePendingDelete.templateType)}
              </div>
              <div className="ftmDeleteText">
                Are you sure you want to delete this template? Existing messages will keep their saved form snapshot,
                but the template will no longer be available for new forms.
              </div>
            </div>
          </div>
        ) : (
          undefined
        )
      }
    />
  );

  const unsavedChangesModal = (
    <UnsavedChangesModal
      isOpen={!!pendingExitPath}
      message="You have unsaved changes in this form template. If you leave now, those changes will be lost."
      onKeepEditing={() => setPendingExitPath('')}
      onDiscard={discardChanges}
    />
  );

  const renderTagBadge = (tag: string, onRemove?: () => void, onClick?: () => void) => {
    const content = (
      <>
        <span className="ftmTagDot" />
        <span>{tag}</span>
        {onRemove ? (
          <button type="button" className="ftmTagRemove" onClick={onRemove} aria-label={`Remove ${tag}`}>
            x
          </button>
        ) : null}
      </>
    );

    if (onClick) {
      return (
        <button
          key={tag}
          type="button"
          className="ftmTagChip ftmTagChipButton"
          style={getTagStyleForName(tag)}
          onClick={onClick}
        >
          {content}
        </button>
      );
    }

    return (
      <span key={tag} className="ftmTagChip" style={getTagStyleForName(tag)}>
        {content}
      </span>
    );
  };

  if (loading && !templates.length) return <Loader />;

  if (editingTemplate && definition) {
    const typeConfig = getConfig(editingTemplate.templateType);

    return (
      <div className="ftmBuilderPage">
        {deleteConfirmationModal}
        {unsavedChangesModal}
        <div className="ftmBuilderTopbar">
          <button
            type="button"
            className="ftmBuilderBackLink"
            onClick={closeEditor}
            disabled={saving}
          >
            <span aria-hidden="true">‹</span> Back to forms
          </button>
          <span className="ftmBuilderBackDivider" aria-hidden="true" />
          <input
            className="ftmBuilderName"
            value={name}
            onChange={event => {
              setName(event.target.value);
              setIsEditorDirty(true);
            }}
          />
          <span className="ftmTypeBadge ftmTypeBadge--info">
            <FontAwesomeIcon icon={typeConfig.icon} /> {typeConfig.label}
          </span>
          {editingTemplate.isDefault ? <span className="ftmStatusBadge ftmStatusBadge--default">Default</span> : null}
          <span className="ftmBuilderMeta">{countQuestions(definition)} questions</span>
          <div className="ftmBuilderSpacer" />
          {!editingTemplate.isDefault ? (
            <button
              type="button"
              className="ftmButton ftmButton--secondary"
              onClick={() => save(true)}
              disabled={saving}
            >
              Save and make default
            </button>
          ) : null}
          <button
            type="button"
            className="ftmButton ftmButton--dangerGhost"
            onClick={() => requestRemove(editingTemplate)}
            disabled={saving}
          >
            <FontAwesomeIcon icon={faTrash} /> Delete
          </button>
          <button type="button" className="ftmButton ftmButton--primary" onClick={() => save(false)} disabled={saving}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>

        {error ? <div className="ftmBanner ftmBanner--warning">{error}</div> : null}

        <div className="ftmBuilderSettings">
          <div className="ftmField">
            <label>Template name</label>
            <input
              value={name}
              onChange={event => {
                setName(event.target.value);
                setIsEditorDirty(true);
              }}
            />
          </div>
          <div className="ftmField">
            <label>Template type</label>
            <select value={editingTemplate.templateType} disabled>
              <option value={editingTemplate.templateType}>{typeConfig.label}</option>
            </select>
          </div>
          <div className="ftmRolesPanel">
            <div className="ftmFieldLabel">Roles that can edit</div>
            <div className="ftmRoleChips">
              {TEMPLATE_ROLE_OPTIONS.map(role => {
                const checked = editableByRoles.includes(role);
                return (
                  <button
                    key={role}
                    type="button"
                    className={`ftmRoleChip ${checked ? 'isActive' : ''}`}
                    onClick={() => {
                      const next = checked ? editableByRoles.filter(item => item !== role) : [...editableByRoles, role];
                      setEditableByRoles(next.length ? next : ['ADMIN']);
                      setIsEditorDirty(true);
                    }}
                  >
                    {role.charAt(0) + role.slice(1).toLowerCase()}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="ftmTagsPanel">
            <div className="ftmFieldLabel">Tags</div>
            <div className="ftmTagPicker" role="group" aria-label="Template tags">
              {availableTags
                .slice()
                .sort((a, b) => a.tagName.localeCompare(b.tagName))
                .map(tag => {
                  const selected = templateTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      className={selected ? 'isSelected' : ''}
                      onClick={() => {
                        setTemplateTagIds(previous =>
                          selected ? previous.filter(tagId => tagId !== tag.id) : normalizeTagIds([...previous, tag.id])
                        );
                        setIsEditorDirty(true);
                      }}
                    >
                      <span className="ftmTagDot" style={{ color: tag.color }} />
                      {tag.tagName}
                    </button>
                  );
                })}
              {!availableTags.length ? <span className="ftmMuted">No tags available</span> : null}
            </div>
          </div>
        </div>

        <div
          className="ftmBuilderSurface"
          onPointerDownCapture={() => {
            builderUserInteractedRef.current = true;
          }}
          onKeyDownCapture={() => {
            builderUserInteractedRef.current = true;
          }}
        >
          <FormBuilderV2
            definition={definition}
            onChange={next => {
              setDefinition(next);
              if (builderUserInteractedRef.current) setIsEditorDirty(true);
              setError('');
            }}
            uploadImage={file => uploadFormImage(user, file)}
            uploadFile={file => uploadFormFile(user, file)}
          />
        </div>
      </div>
    );
  }
  return (
    <div className="ftmPage ftmPage--modern">
      <SimpleModal
        isOpen={isHelpOpen}
        title="How form builder works"
        onCloseClick={() => setIsHelpOpen(false)}
        buttonCancelText="Close"
        body={
          <div className="ftmHelpContent">
            <div>
              <strong>Template library</strong>
              <span>Build reusable forms for consent requests, club bookings and tournament confirmations.</span>
            </div>
            <div>
              <strong>Defaults</strong>
              <span>Mark the most common template as default for faster sending.</span>
            </div>
            <div>
              <strong>Tags</strong>
              <span>Use tags to keep larger template libraries easy to scan and filter.</span>
            </div>
          </div>
        }
      />
      <SimpleModal
        isOpen={isCreateTypeOpen}
        title="Choose template type"
        onCloseClick={() => setIsCreateTypeOpen(false)}
        buttonCancelText="Cancel"
        body={
          <div className="ftmCreateTypes">
            {activeTypes.map(item => {
              const config = getConfig(item);
              return (
                <button
                  key={item}
                  type="button"
                  className="ftmTypeChoice"
                  onClick={() => create(item)}
                  disabled={saving}
                >
                  <span className="ftmTypeIcon" style={{ backgroundColor: `${config.color}1a`, color: config.color }}>
                    <FontAwesomeIcon icon={config.icon} />
                  </span>
                  <span>
                    <strong>{config.label}</strong>
                    <small>{getDefaultName(item)}</small>
                  </span>
                </button>
              );
            })}
          </div>
        }
      />

      <SimpleModal
        isOpen={!!tagModalMode}
        title={tagModalMode === 'EDIT' ? 'Edit tag' : 'Create tag'}
        onCloseClick={closeTagModal}
        onBackdropClick={closeTagModal}
        buttonCancelText="Cancel"
        buttonText={
          saving ? 'Saving...' : tagModalMode === 'EDIT' ? 'Save changes' : 'Create tag'
        }
        buttonDisabled={
          saving || !(tagModalMode === 'EDIT' ? normalizeTag(renameValue) : normalizeTag(newTagName))
        }
        onButtonClick={
          tagModalMode === 'EDIT'
            ? () => {
                if (!saving && normalizeTag(renameValue)) renameTag(renamingTag);
              }
            : () => {
                if (!saving && normalizeTag(newTagName)) createTag();
              }
        }
        customClass="ftmTagModal"
        dialogStyle={{ maxWidth: 520 }}
        isBodyBold={false}
        body={
          <div className="ftmTagModalBody">
            <div className="ftmField">
              <label htmlFor="ftm-tag-name">Tag name</label>
              <input
                id="ftm-tag-name"
                value={tagModalMode === 'EDIT' ? renameValue : newTagName}
                maxLength={40}
                autoFocus
                onChange={event =>
                  tagModalMode === 'EDIT'
                    ? setRenameValue(event.target.value)
                    : setNewTagName(event.target.value)
                }
                onKeyDown={event => {
                  if (event.key === 'Escape') closeTagModal();
                  if (event.key === 'Enter' && !saving) {
                    if (tagModalMode === 'EDIT') renameTag(renamingTag);
                    else createTag();
                  }
                }}
                placeholder="e.g. Swimming"
              />
            </div>
            <div className="ftmField">
              <label>Colour</label>
              <div className="ftmColorControl">
                <input
                  type="color"
                  value={tagModalMode === 'EDIT' ? renameColor : newTagColor}
                  onChange={event =>
                    tagModalMode === 'EDIT'
                      ? setRenameColor(event.target.value)
                      : setNewTagColor(event.target.value)
                  }
                />
                <span>{(tagModalMode === 'EDIT' ? renameColor : newTagColor).toUpperCase()}</span>
              </div>
              <div className="ftmColorPresets">
                {TAG_COLOR_PRESETS.map(color => {
                  const selectedColor = tagModalMode === 'EDIT' ? renameColor : newTagColor;
                  return (
                    <button
                      key={color}
                      type="button"
                      className={selectedColor.toUpperCase() === color ? 'isSelected' : ''}
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        tagModalMode === 'EDIT' ? setRenameColor(color) : setNewTagColor(color)
                      }
                      aria-label={`Use ${color}`}
                    />
                  );
                })}
              </div>
            </div>
            <div className="ftmField ftmTagPreviewField">
              <label>Preview</label>
              <div>
                {tagModalMode === 'EDIT'
                  ? renameValue
                    ? renderTagBadge(renameValue)
                    : <span className="ftmMuted">Enter a tag name</span>
                  : newTagName
                  ? renderTagBadge(newTagName)
                  : <span className="ftmTagChip ftmTagChip--previewDisabled"><span className="ftmTagDot" />Example tag</span>}
              </div>
            </div>
          </div>
        }
      />

      <UnsavedChangesModal
        isOpen={isTagDiscardOpen}
        message="You have unsaved changes to this tag. If you close it now, those changes will be lost."
        onKeepEditing={() => setIsTagDiscardOpen(false)}
        onDiscard={discardTagChanges}
      />

      <SimpleModal
        isOpen={!!detailsTemplate}
        title="Template details"
        onCloseClick={() => setDetailsTemplate(undefined)}
        buttonCancelText="Close"
        body={
          detailsTemplate
            ? (() => {
                const detailDefinition = toDefinition(
                  detailsTemplate,
                  getDefinitionTitle(detailsTemplate.templateType)
                );
                const tags = getTemplateTagNames(detailsTemplate);
                const config = getConfig(detailsTemplate.templateType);
                return (
                  <div className="ftmDetailsGrid">
                    <div className="ftmDetailsHero">
                      <span
                        className="ftmTypeIcon"
                        style={{ backgroundColor: `${config.color}1a`, color: config.color }}
                      >
                        <FontAwesomeIcon icon={config.icon} />
                      </span>
                      <div>
                        <div className="ftmDetailsTitle">
                          {detailsTemplate.name || getDefaultName(detailsTemplate.templateType)}
                        </div>
                        <div className="ftmDetailsMeta">
                          <span>{config.label}</span>
                          {detailsTemplate.isDefault ? (
                            <span className="ftmStatusBadge ftmStatusBadge--default">Default</span>
                          ) : null}
                          <span>{countQuestions(detailDefinition)} questions</span>
                        </div>
                      </div>
                    </div>
                    <div className="ftmDetailsCard">
                      <div className="ftmDetailsLabel">Created by</div>
                      <div>
                        {getAuditUserText(detailsTemplate.createdByUserName, detailsTemplate.createdByUserEmail)}
                      </div>
                    </div>
                    <div className="ftmDetailsCard">
                      <div className="ftmDetailsLabel">Created at</div>
                      <div>{getCreatedAtWithTime(detailsTemplate) || '-'}</div>
                    </div>
                    <div className="ftmDetailsCard">
                      <div className="ftmDetailsLabel">Updated by</div>
                      <div>
                        {getAuditUserText(detailsTemplate.updatedByUserName, detailsTemplate.updatedByUserEmail)}
                      </div>
                    </div>
                    <div className="ftmDetailsCard">
                      <div className="ftmDetailsLabel">Updated at</div>
                      <div>{getUpdatedAtWithTime(detailsTemplate) || '-'}</div>
                    </div>
                    <div className="ftmDetailsCard ftmDetailsCard--wide">
                      <div className="ftmDetailsLabel">Tags</div>
                      <div className="ftmTags">
                        {tags.length ? tags.map(tag => renderTagBadge(tag)) : <span className="ftmMuted">No tags</span>}
                      </div>
                    </div>
                  </div>
                );
              })()
            : undefined
        }
      />

      {deleteConfirmationModal}

      <div className="ftmHeader">
        <div className="sitPageTitleWithHelp">
          <div>
            <h1>Form builder</h1>
          </div>
          <HelpIconButton onClick={() => setIsHelpOpen(true)} label="How form builder works" />
        </div>
        <nav className="ftmTabs" aria-label="Form builder sections">
          <button
            type="button"
            className={activeTab === 'TEMPLATES' ? 'isActive' : ''}
            onClick={() => setActiveTab('TEMPLATES')}
          >
            Templates <span>{templates.length}</span>
          </button>
          <button type="button" className={activeTab === 'TAGS' ? 'isActive' : ''} onClick={() => setActiveTab('TAGS')}>
            Tags <span>{availableTags.length}</span>
          </button>
        </nav>
        <div className="ftmHeaderActions">
          {activeTab === 'TEMPLATES' ? (
            <button
              type="button"
              className="ftmButton ftmButton--primary"
              onClick={onNewTemplateClick}
              disabled={saving}
            >
              <FontAwesomeIcon icon={faPlus} /> New template
            </button>
          ) : null}
        </div>
      </div>

      <div className="ftmMainCard">
      {error && !editingKey ? <div className="ftmBanner ftmBanner--warning">{error}</div> : null}

      {activeTab === 'TEMPLATES' ? (
        <>
          <div className="ftmTypeGrid">
            {activeTypes.map(item => {
              const config = getConfig(item);
              const count = templates.filter(template => template.templateType === item).length;
              const active = filterType === item;
              return (
                <button
                  key={item}
                  type="button"
                  className={`ftmTypeCard ${active ? 'isActive' : ''}`}
                  onClick={() => setFilterType(filterType === item ? 'ALL' : item)}
                >
                  <span className="ftmTypeIcon" style={{ backgroundColor: `${config.color}1a`, color: config.color }}>
                    <FontAwesomeIcon icon={config.icon} />
                  </span>
                  <span>
                    <strong>{config.label}</strong>
                    <small>
                      {count} template{count === 1 ? '' : 's'}
                    </small>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="ftmControls">
            <div className="ftmSearchField">
              <input
                value={filterName}
                onChange={event => setFilterName(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    setAppliedFilterName(filterName);
                  }
                }}
                placeholder="Search templates..."
              />
            </div>
            <button
              type="button"
              className={`ftmButton ftmButton--secondary ftmFilterButton${
                activeDrawerFilterCount ? ' isActive' : ''
              }`}
              onClick={openFilters}
            >
              <span className="ftmFilterIcon" aria-hidden="true" />
              Filters
              {activeDrawerFilterCount ? <span className="ftmFilterCount">{activeDrawerFilterCount}</span> : null}
            </button>
            <div className="ftmColumns" ref={columnsRef}>
              <button
                type="button"
                className="ftmButton ftmButton--secondary"
                onClick={() => setIsColumnsOpen(!isColumnsOpen)}
              >
                <span className="ftmColumnsIcon" /> Columns
                <span className="ftmFilterCount">{visibleColumnCount}</span>
              </button>
              {isColumnsOpen ? (
                <div className="ftmColumnsMenu">
                  <div className="ftmColumnsHeader">
                    <span>Show / hide columns</span>
                    <button type="button" onClick={() => persistHiddenColumns([])}>
                      Reset
                    </button>
                  </div>
                  {TEMPLATE_COLUMNS.map(column => {
                    const visible = column.locked || isColumnVisible(column.field);
                    return (
                      <label key={column.field} className={column.locked ? 'isLocked' : ''}>
                        <input
                          type="checkbox"
                          checked={visible}
                          disabled={column.locked}
                          onChange={() => toggleColumn(column.field)}
                        />
                        <span>{column.label}</span>
                      </label>
                    );
                  })}
                </div>
              ) : null}
            </div>
            {isFiltered ? (
              <button
                type="button"
                className="ftmButton ftmButton--ghost"
                onClick={() => {
                  setFilterName('');
                  setFilterType('ALL');
                  setFilterStatus('ALL');
                  setFilterTag('ALL');
                }}
              >
                Clear filters
              </button>
            ) : null}
            <div className="ftmControlsSpacer" />
          </div>

          {isFilterOpen ? (
            <div
              className="ftmFilterOverlay"
              onMouseDown={event => {
                if (event.target === event.currentTarget) closeFilters();
              }}
            >
              <form
                className="ftmFilterDrawer"
                aria-label="Template filters"
                onSubmit={event => {
                  event.preventDefault();
                  applyFilters();
                }}
              >
                <div className="ftmFilterHeader">
                  <div>
                    <h2>Filters</h2>
                  </div>
                  <button
                    type="button"
                    className="ftmIconButton"
                    onClick={closeFilters}
                    aria-label="Close filters"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div className="ftmFilterBody">
                  <div className="ftmFilterSection">
                    <div className="ftmFieldLabel">Status</div>
                    <div className="ftmFilterChips">
                      {(['ALL', 'DEFAULT', 'CUSTOM'] as TemplateStatusFilter[]).map(status => (
                        <button
                          key={status}
                          type="button"
                          className={draftFilterStatus === status ? 'isActive' : ''}
                          onClick={() => setDraftFilterStatus(status)}
                        >
                          {status === 'ALL' ? 'All' : status === 'DEFAULT' ? 'Default' : 'Custom'}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="ftmFilterSection">
                    <div className="ftmFieldLabel">Tags</div>
                    <div className="ftmFilterChips ftmFilterChips--tags">
                      <button
                        type="button"
                        className={draftFilterTag === 'ALL' ? 'isActive' : ''}
                        onClick={() => setDraftFilterTag('ALL')}
                      >
                        All
                      </button>
                      {availableTags
                        .slice()
                        .sort((a, b) => a.tagName.localeCompare(b.tagName))
                        .map(tag => (
                        <button
                          key={tag.id}
                          type="button"
                          className={draftFilterTag === tag.id ? 'isActive' : ''}
                          onClick={() => setDraftFilterTag(tag.id)}
                        >
                          {tag.tagName}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="ftmFilterFooter">
                  <button
                    type="button"
                    className="ftmButton ftmButton--ghost"
                    onClick={() => {
                      setDraftFilterStatus('ALL');
                      setDraftFilterTag('ALL');
                    }}
                  >
                    Clear all
                  </button>
                  <div>
                    <button type="button" className="ftmButton ftmButton--secondary" onClick={closeFilters}>Cancel</button>
                    <button type="submit" className="ftmButton ftmButton--primary">Apply</button>
                  </div>
                </div>
              </form>
            </div>
          ) : null}

          <div className="ftmCard ftmTableCard">
            {filteredTemplates.length ? (
              <table className="ftmTable">
                <thead>
                  <tr>
                    <th>Template</th>
                    {isColumnVisible('type') ? <th>Type</th> : null}
                    {isColumnVisible('tags') ? <th>Tags</th> : null}
                    {isColumnVisible('status') ? <th>Status</th> : null}
                    {isColumnVisible('updatedAt') ? <th>Last updated</th> : null}
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {pageTemplates.map(template => {
                    const templateDefinition = toDefinition(template, getDefinitionTitle(template.templateType));
                    const tags = getTemplateTagNames(template);
                    const config = getConfig(template.templateType);
                    return (
                      <tr key={getTemplateKey(template)} onClick={() => setDetailsTemplate(template)}>
                        <td>
                          <div className="ftmNameCell">
                            <span
                              className="ftmNameIcon"
                              style={{ backgroundColor: `${config.color}1a`, color: config.color }}
                            >
                              <FontAwesomeIcon icon={config.icon} />
                            </span>
                            <span>
                              <strong>{template.name || getDefaultName(template.templateType)}</strong>
                              <small>{countQuestions(templateDefinition)} questions</small>
                            </span>
                          </div>
                        </td>
                        {isColumnVisible('type') ? <td>{config.label}</td> : null}
                        {isColumnVisible('tags') ? (
                          <td>
                            <div className="ftmTags">
                              {tags.length ? (
                                tags.map(tag => renderTagBadge(tag, undefined, () => setFilterTag(tag)))
                              ) : (
                                <span className="ftmMuted">-</span>
                              )}
                            </div>
                          </td>
                        ) : null}
                        {isColumnVisible('status') ? (
                          <td>
                            {template.isDefault ? (
                              <span className="ftmStatusBadge ftmStatusBadge--default">Default</span>
                            ) : (
                              <span className="ftmStatusBadge">Custom</span>
                            )}
                          </td>
                        ) : null}
                        {isColumnVisible('updatedAt') ? (
                          <td>
                            <div className="ftmAudit">
                              <strong>{getUpdatedAtWithTime(template) || getCreatedAtWithTime(template) || '-'}</strong>
                              <span>
                                by{' '}
                                {getAuditUserText(
                                  template.updatedByUserName || template.createdByUserName,
                                  template.updatedByUserEmail || template.createdByUserEmail
                                )}
                              </span>
                            </div>
                          </td>
                        ) : null}
                        <td>
                          <div
                            className="ftmRowActions"
                            onClick={event => event.stopPropagation()}
                            ref={actionMenuKey === getTemplateKey(template) ? actionMenuRef : undefined}
                          >
                            <button
                              type="button"
                              className="ftmRowEditButton"
                              onClick={() => openEditor(template)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="ftmRowMenuButton"
                              aria-label={`Actions for ${template.name || getDefaultName(template.templateType)}`}
                              aria-haspopup="menu"
                              aria-expanded={actionMenuKey === getTemplateKey(template)}
                              onClick={() =>
                                setActionMenuKey(current =>
                                  current === getTemplateKey(template) ? '' : getTemplateKey(template)
                                )
                              }
                            >
                              <span aria-hidden="true">•••</span>
                            </button>
                            {actionMenuKey === getTemplateKey(template) ? (
                              <div className="ftmRowMenu" role="menu">
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={() => {
                                    setActionMenuKey('');
                                    duplicateTemplate(template);
                                  }}
                                  disabled={saving}
                                >
                                  Copy
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="isDanger"
                                  onClick={() => {
                                    setActionMenuKey('');
                                    requestRemove(template);
                                  }}
                                  disabled={saving}
                                >
                                  Delete
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="ftmEmpty">
                <FontAwesomeIcon icon={faFileAlt} />
                <strong>No templates match</strong>
                <span>Try another type, tag or search term.</span>
              </div>
            )}
          </div>
          <div className="ftmPagination">
            <Pagination2
              currentPage={currentPage}
              setCurrentPageParams={setCurrentPage}
              total={filteredTemplates.length}
              limit={TEMPLATE_PAGE_LIMIT}
              modern={true}
            />
          </div>
        </>
      ) : (
        <div className="ftmCard ftmTagListCard">
          <div className="ftmToolbar ftmTagToolbar">
            <div className="ftmSearchField ftmTagSearch">
              <input
                value={tagSearch}
                onChange={event => setTagSearch(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    setAppliedTagSearch(tagSearch);
                  }
                }}
                placeholder="Search tags..."
                aria-label="Search tags"
              />
              {tagSearch ? (
                <button
                  type="button"
                  onClick={() => {
                    setTagSearch('');
                    setAppliedTagSearch('');
                  }}
                  aria-label="Clear tag search"
                >
                  ×
                </button>
              ) : null}
            </div>
            <button type="button" className="ftmButton ftmButton--primary" onClick={openCreateTagModal}>
              <FontAwesomeIcon icon={faPlus} /> Create tag
            </button>
          </div>
          <div className="ftmTableWrap">
            {visibleTags.length ? (
              <table className="ftmTable ftmTagTable">
                <thead>
                  <tr>
                    <th>Tag</th>
                    <th>Used by</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleTags.map(tag => {
                  const count = templates.filter(template =>
                    getTemplateTagNames(template).some(item => item.toLowerCase() === tag.toLowerCase())
                  ).length;
                  const tagMenuKey = `tag:${tag}`;
                  return (
                    <tr key={tag}>
                      <td>
                        {renderTagBadge(tag, undefined, () => {
                          setActiveTab('TEMPLATES');
                          setFilterTag(tag);
                        })}
                      </td>
                      <td><span className="ftmTagUsage">{count} template{count === 1 ? '' : 's'}</span></td>
                      <td>
                        <div
                          className="ftmRowActions"
                          onClick={event => event.stopPropagation()}
                          ref={actionMenuKey === tagMenuKey ? actionMenuRef : undefined}
                        >
                          <button
                            type="button"
                            className="ftmRowEditButton"
                            onClick={() => openEditTagModal(tag)}
                            disabled={saving}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="ftmRowMenuButton"
                            aria-label={`Actions for ${tag}`}
                            aria-haspopup="menu"
                            aria-expanded={actionMenuKey === tagMenuKey}
                            onClick={() => setActionMenuKey(current => current === tagMenuKey ? '' : tagMenuKey)}
                          >
                            <span aria-hidden="true">•••</span>
                          </button>
                          {actionMenuKey === tagMenuKey ? (
                            <div className="ftmRowMenu" role="menu">
                              <button
                                type="button"
                                role="menuitem"
                                className="isDanger"
                                onClick={() => {
                                  setActionMenuKey('');
                                  deleteTag(tag);
                                }}
                                disabled={saving}
                              >
                                Delete
                              </button>
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="ftmEmpty">
                <FontAwesomeIcon icon={faTags} />
                <strong>{appliedTagSearch ? 'No tags match' : 'No template tags yet'}</strong>
                <span>{appliedTagSearch ? 'Try another tag name.' : 'Create tags here, then assign them to templates.'}</span>
              </div>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
};
